<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeForm">
    <div class="form-modal">
      <button class="close-btn" @click="closeForm">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>

      <div class="form-header">
        <span class="header-label">RESERVATION</span>
        <h3>방문예약</h3>
      </div>

      <form @submit.prevent="submitForm" class="reservation-form">
        <div class="form-group">
          <input 
            type="text" 
            v-model="form.name" 
            placeholder="성함"
            required
            :disabled="isSubmitting"
          />
        </div>

        <div class="form-group">
          <PhoneInput v-model="form.phone" :disabled="isSubmitting" required />
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="form.agreePrivacy"
              required
              :disabled="isSubmitting"
            />
            <span class="checkmark"></span>
            <span class="checkbox-text">
              개인정보 수집 및 이용 동의
              <button type="button" class="privacy-link" @click="showPrivacy = true">보기</button>
            </span>
          </label>
        </div>

        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          {{ isSubmitting ? '신청 중...' : '예약 신청' }}
        </button>
      </form>
    </div>

    <!-- 개인정보처리방침 모달 -->
    <div v-if="showPrivacy" class="privacy-overlay" @click.self="showPrivacy = false">
      <div class="privacy-modal">
        <h4>개인정보 수집 및 이용 동의</h4>
        <div class="privacy-content">
          <p><strong>수집 항목</strong><br>성함, 연락처</p>
          <p><strong>수집 목적</strong><br>방문 상담 예약 및 안내</p>
          <p><strong>보유 기간</strong><br>목적 달성 후 즉시 파기</p>
        </div>
        <button @click="showPrivacy = false" class="privacy-close">확인</button>
      </div>
    </div>
  </div>
</template>

<script>
import { saveConsultation } from '@/firebase/consultationService'
import PhoneInput from '@/components/common/PhoneInput.vue'

export default {
  name: 'MobileReservationModal',
  components: { PhoneInput },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      showPrivacy: false,
      isSubmitting: false,
      form: {
        name: '',
        phone: '',
        agreePrivacy: false
      }
    }
  },
  watch: {
    isOpen(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  methods: {
    closeForm() {
      this.$emit('close')
    },
    async submitForm() {
      if (!this.form.agreePrivacy) {
        alert('개인정보 수집 및 이용에 동의해주세요.')
        return
      }

      this.isSubmitting = true

      try {
        const result = await saveConsultation({
          name: this.form.name,
          phone: this.form.phone
        }, 'mobile')

        if (result.success) {
          alert('예약 신청이 완료되었습니다.')
          
          this.form = {
            name: '',
            phone: '',
            agreePrivacy: false
          }
          this.closeForm()
        } else {
          alert('오류가 발생했습니다. 다시 시도해주세요.')
        }
      } catch (error) {
        console.error('Submit error:', error)
        alert('오류가 발생했습니다. 다시 시도해주세요.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
}

.form-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 340px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #eee;
}

.close-btn svg {
  width: 16px;
  height: 16px;
  color: #999;
}

.form-header {
  padding: 32px 24px 24px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.header-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--main-color1);
  margin-bottom: 8px;
}

.form-header h3 {
  font-size: 22px;
  font-weight: 700;
  color: #222;
  letter-spacing: -0.5px;
}

.reservation-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  background: #fafafa;
  transition: all 0.2s;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #aaa;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--main-color1);
  background: #fff;
}

.form-group input:disabled,
.form-group textarea:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-group textarea {
  resize: none;
}

.form-group :deep(.phone-segment) {
  padding: 14px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  background: #fafafa;
  transition: all 0.2s;
}

.form-group :deep(.phone-segment:focus) {
  outline: none;
  border-color: var(--main-color1);
  background: #fff;
}

.form-group :deep(.phone-segment:disabled) {
  background: #f5f5f5;
  cursor: not-allowed;
}

/* 체크박스 */
.checkbox-group {
  margin-top: 20px;
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkmark {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1.5px solid #ccc;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s;
}

.checkbox-label input:checked + .checkmark {
  background: var(--main-color1);
  border-color: var(--main-color1);
}

.checkbox-label input:checked + .checkmark::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-text {
  display: flex;
  align-items: center;
  gap: 6px;
}

.privacy-link {
  background: none;
  border: none;
  color: var(--main-color1);
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  padding: 0;
}

.submit-btn {
  width: 100%;
  padding: 16px;
  background: #222;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 개인정보 모달 */
.privacy-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1001;
}

.privacy-modal {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 300px;
  width: 100%;
}

.privacy-modal h4 {
  font-size: 15px;
  margin-bottom: 16px;
  color: #222;
  font-weight: 600;
}

.privacy-content p {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 12px;
}

.privacy-content p strong {
  color: #333;
  font-weight: 600;
}

.privacy-close {
  width: 100%;
  padding: 12px;
  background: #222;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
}
</style>